import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '@/shared/store';
import { getVaultPools } from '@/shared/store/vaults/actions';

type UseFetchVaultPools = {
    vaultAddress: string;
    marginAccountAddress: string;
};

export const useFetchVaultPools = ({ vaultAddress, marginAccountAddress }: UseFetchVaultPools) => {
    const { linkedPools, linkedPoolsLoading, error } = useSelector((state: Store) => ({
        linkedPools: state.vaults.linkedPools,
        linkedPoolsLoading: state.vaults.linkedPoolsLoading,
        error: state.errors.vaults,
    }));

    const dispatch = useDispatch();
    const hasDispatchedRef = useRef<boolean>(false);

    const fetchVaultPools = useCallback(() => {
        dispatch(
            getVaultPools({
                vaultAddress,
                marginAccountAddress,
                withoutCache: true,
            }),
        );
    }, [vaultAddress, marginAccountAddress]);

    const shouldFetch = useMemo(() => {
        return !(!vaultAddress || !marginAccountAddress || linkedPools[vaultAddress]);
    }, [vaultAddress, marginAccountAddress]);

    useEffect(() => {
        if (shouldFetch && !hasDispatchedRef.current) {
            fetchVaultPools();
            hasDispatchedRef.current = true;
        }
    }, [shouldFetch]);

    const pools = linkedPools[vaultAddress] || [];

    return { pools, loading: linkedPoolsLoading, error, fetchVaultPools };
};
